import { useTranslation } from "@shared/utils/internationalization";
import Button, { ButtonProps } from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "../../typography/Text/Text.web";
import { ProgressIndicatorSharedProps } from "./ProgressIndicator.props";

export type ProgressIndicatorProps = ProgressIndicatorSharedProps;

/**
 * Basic UI component for displaying progression of steps to the user.
 * This component doesn't handle any state or logic - this must be handled by the parent component.
 */
export default function ProgressIndicator({
  steps,
  currentIndex,
  goToStep,
  variant = "dynamic",
  disableNavigation = false,
}: ProgressIndicatorSharedProps): JSX.Element {
  const filteredSteps = steps.filter((step) => !step.hidden);

  if (variant === "dynamic") {
    return (
      <>
        <Box display={{ base: "flex", sm: "none" }}>
          <ProgressIndicator.Condensed
            steps={filteredSteps}
            currentIndex={currentIndex}
            goToStep={goToStep}
            disableNavigation={disableNavigation}
          />
        </Box>
        <Box display={{ base: "none", sm: "block" }}>
          <ProgressIndicator.Breadcrumbs
            steps={filteredSteps}
            currentIndex={currentIndex}
            goToStep={goToStep}
            disableNavigation={disableNavigation}
          />
        </Box>
      </>
    );
  }

  if (variant === "condensed") {
    return (
      <ProgressIndicator.Condensed
        steps={filteredSteps}
        currentIndex={currentIndex}
        goToStep={goToStep}
        disableNavigation={disableNavigation}
      />
    );
  }

  if (variant === "breadcrumbs") {
    return (
      <ProgressIndicator.Breadcrumbs
        steps={filteredSteps}
        currentIndex={currentIndex}
        goToStep={goToStep}
        disableNavigation={disableNavigation}
      />
    );
  }

  return <></>;
}

ProgressIndicator.Condensed = Condensed;

function Condensed({
  steps,
  currentIndex,
  goToStep,
  disableNavigation,
}: {
  steps: ProgressIndicatorProps["steps"];
  currentIndex: ProgressIndicatorProps["currentIndex"];
  goToStep: ProgressIndicatorProps["goToStep"];
  disableNavigation: ProgressIndicatorProps["disableNavigation"];
}) {
  const { t } = useTranslation("common");
  return (
    <Box
      display="flex"
      alignItems="center"
      data-testid="progress-indicator-mobile-view"
      gap="xs"
    >
      {currentIndex > 0 && Boolean(steps.length) && !disableNavigation && (
        <Button.Ghost
          data-testid="progress-indicator-back-button"
          onClick={() => goToStep(steps[currentIndex - 1].id)}
          pr="none"
          pl="none"
          height="element.xl"
          ml="-14px"
          mr="2xs"
        >
          <Icon.ChevronLeft color="fg" />
        </Button.Ghost>
      )}
      <Box display="flex" alignItems="baseline" gap="2xs">
        <Text
          color="fg"
          fontWeight="bold"
          fontSize={{ base: "md", sm: "xl" }}
          maxWidth="md"
          display={{ base: "none", sm: "block" }}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          lineHeight="2xl"
        >
          {steps[currentIndex]?.name || ""}
        </Text>
        <Text color="fg.muted" lineHeight={1} fontWeight="400" fontSize="md">
          {t("{{count}} of {{total}}", {
            count: !Number.isNaN(currentIndex + 1) ? currentIndex + 1 : 1,
            total: steps.length,
          })}
        </Text>
      </Box>
    </Box>
  );
}

ProgressIndicator.Breadcrumbs = Breadcrumbs;

function Breadcrumbs({
  steps,
  currentIndex,
  goToStep,
  disableNavigation,
}: {
  steps: ProgressIndicatorProps["steps"];
  currentIndex: ProgressIndicatorProps["currentIndex"];
  goToStep: ProgressIndicatorProps["goToStep"];
  disableNavigation: ProgressIndicatorProps["disableNavigation"];
}) {
  const isLastStep = (index: number) => {
    const visibleSteps = steps.filter((step) => !step.hidden);
    return index === visibleSteps.length - 1;
  };
  const disabledStep = (index: number) =>
    disableNavigation || index >= currentIndex;

  return (
    <Box data-testid="progress-indicator-desktop-view">
      {steps?.map((step, index) => (
        <span key={step.id || index}>
          {!step.hidden && (
            <>
              <Breadcrumbs.Breadcrumb
                isCurrent={index === currentIndex}
                onClick={
                  index === currentIndex ? undefined : () => goToStep(step.id)
                }
                isDisabled={index === currentIndex ? true : disabledStep(index)}
                data-testid={`progress-indicator-desktop-${step.id}`}
                fontWeight={index <= currentIndex ? "bold" : "normal"}
                ml={index > currentIndex ? "0.04em" : "0em"}
                mr={index > currentIndex ? "0.04em" : "0em"}
              >
                {step.name}
              </Breadcrumbs.Breadcrumb>
              {!isLastStep(index) && (
                <Icon.ChevronRight
                  color={disabledStep(index) ? "gray.400" : "gray.600"}
                />
              )}
            </>
          )}
        </span>
      ))}
    </Box>
  );
}

type BreadcrumbProps = ButtonProps & {
  isCurrent?: boolean;
};

Breadcrumbs.Breadcrumb = function Breadcrumb(
  props: BreadcrumbProps
): JSX.Element {
  const { isCurrent, ...rest } = props;
  // Display for current step
  if (isCurrent) {
    return (
      <Button
        color="primary.900"
        fontWeight="bold"
        _disabled={{
          bg: "transparent",
          color: "primary.600",
          opacity: 1,
        }}
        isDisabled
        pr="2xs"
        pl="2xs"
        {...rest}
      />
    );
  }

  // Display for remaining steps
  return (
    <Button
      color="gray.600"
      bg="transparent"
      fontWeight="normal"
      _active={{
        color: "gray.600",
      }}
      _hover={{
        color: "gray.600",
        textDecoration: "underline",
      }}
      _disabled={{
        color: "gray.400",
        cursor: "default",
        opacity: 1,
        _hover: {
          color: "gray.400",
          textDecoration: "none",
        },
      }}
      pr="2xs"
      pl="2xs"
      {...rest}
    />
  );
};
